import { useCallback, useEffect, useState } from "react";
import { BookPlus, CheckCircle2, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { manualUrl } from "@/components/HelpButton";
import FaqDraftDialog from "./FaqDraftDialog";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("nl-NL", { day: "2-digit", month: "short" }) : "—");

export default function TopQuestionsPanel({ refreshKey, onChanged }) {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    try { setData((await api.get("/admin/help/top-questions", { params: { limit: 20 } })).data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div data-testid="top-questions-panel">
      <p className="text-[11px] text-muted-foreground px-3 py-2 border-b border-border">Vergelijkbare vragen zijn gegroepeerd. Met <b>→ FAQ</b> laat je de AI er een regel van maken voor het hoofdstuk <a href={manualUrl("faq")} target="_blank" rel="noreferrer" className="underline">Veelgestelde vragen</a>.</p>
      {!data ? <div className="p-3 text-sm text-muted-foreground">Laden…</div> : data.questions.length === 0 ? (
        <div className="p-3 text-sm text-muted-foreground" data-testid="top-questions-empty">Nog geen vragen met voldoende inhoud.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="top-questions-table">
            <thead className="bg-foreground text-background">
              <tr>{["#", "Vraag", "Aantal", "Laatst", "Status", ""].map((h) => <th key={h} className="text-left px-3 py-2 font-display uppercase text-[10px] tracking-widest">{h}</th>)}</tr>
            </thead>
            <tbody>
              {data.questions.map((q, i) => (
                <tr key={q.key} className="border-t border-border even:bg-muted/30" data-testid={`top-question-row-${i}`}>
                  <td className="px-3 py-2 font-mono-num text-xs text-muted-foreground">{i + 1}</td>
                  <td className="px-3 py-2 max-w-[420px]">
                    <div className="truncate" title={q.question}>{q.question}</div>
                    {q.variants.length > 0 && <div className="text-[11px] text-muted-foreground truncate" title={q.variants.join(" · ")}>ook: {q.variants.join(" · ")}</div>}
                  </td>
                  <td className="px-3 py-2 font-mono-num text-center">{q.count}{q.down > 0 && <span className="ml-1 inline-flex items-center text-destructive text-xs" title={`${q.down}× duim omlaag`}><ThumbsDown className="w-3 h-3" />{q.down}</span>}</td>
                  <td className="px-3 py-2 font-mono-num text-xs whitespace-nowrap">{fmt(q.last_at)}</td>
                  <td className="px-3 py-2 text-xs whitespace-nowrap">
                    {q.faq_added ? <span className="inline-flex items-center gap-1 text-positive" title={q.faq_question || ""} data-testid={`top-question-added-${i}`}><CheckCircle2 className="w-3.5 h-3.5" /> In FAQ</span>
                      : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button onClick={() => setActive(q)} data-testid={`top-question-faq-${i}`}
                      className={`btn-brutal px-2.5 py-1 font-display uppercase font-bold text-[11px] inline-flex items-center gap-1 ${q.faq_added ? "bg-background text-foreground" : "bg-secondary text-secondary-foreground"}`}>
                      <BookPlus className="w-3.5 h-3.5" /> {q.faq_added ? "Nogmaals" : "→ FAQ"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <FaqDraftDialog item={active} onClose={() => setActive(null)} onAdded={() => { load(); onChanged?.(); }} />
    </div>
  );
}
